// Prompt 0454 — Tab Asignaciones extraido + M2.1 tooltip en boton "Asignar persona" deshabilitado.

import type React from 'react';
import { useState } from 'react';
import { request, type PuestoItem } from '../../../../../../shared/services/api';
import { useI18n } from '../../../../../../../i18n';
import { AsignarPersonaSubPanel } from '../components/AsignarPersonaSubPanel';

interface AsignacionesTabProps {
  detalle: PuestoItem;
  onRefresh: () => Promise<void> | void;
}

export function AsignacionesTab({ detalle, onRefresh }: AsignacionesTabProps) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const lbl = (adm ?? {}) as Record<string, string>;
  const panel = (lbl.puestoPanel ?? {}) as unknown as Record<string, string>;

  const asignaciones = (detalle.asignaciones ?? []) as unknown as Array<{
    id: string;
    fechaInicio?: string;
    fechaFin?: string | null;
    activo?: boolean;
    persona?: { id: string; nombre: string; apellidos: string; codigo?: string };
  }>;

  const hayActiva = asignaciones.some((a) => (a.activo ?? true) && !a.fechaFin);
  const personasExcluidas = asignaciones
    .filter((a) => (a.activo ?? true) && !a.fechaFin)
    .map((a) => a.persona?.id)
    .filter((id): id is string => typeof id === 'string');

  const [showSub, setShowSub] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: '#5A6171' }}>
          {panel.asignacionesHeader ?? 'Asignaciones del puesto'}
        </span>
        <button
          onClick={() => !hayActiva && setShowSub(true)}
          disabled={hayActiva}
          title={
            hayActiva
              ? (panel.asignarPersonaTooltipOcupado ??
                'Finaliza la asignacion activa antes de asignar otra persona.')
              : undefined
          }
          style={{
            padding: '6px 14px',
            background: hayActiva ? '#E5E7EB' : '#2F5496',
            color: hayActiva ? '#9CA3AF' : '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: hayActiva ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          + {panel.asignarPersona ?? 'Asignar persona'}
        </button>
      </div>

      {toast && (
        <div
          style={{
            marginBottom: 12,
            padding: '8px 14px',
            background: '#F0FDF4',
            border: '1px solid #BBF7D0',
            borderRadius: 6,
            fontSize: 12,
            color: '#16A34A',
          }}
        >
          {toast}
        </div>
      )}

      {asignaciones.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>
          {panel.sinAsignaciones ?? 'Sin asignaciones'}
        </div>
      ) : (
        <div style={{ border: '1px solid #E5E7EB', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F0F4FA' }}>
                <th style={thStyle}>{panel.colPersona ?? 'Persona'}</th>
                <th style={thStyle}>{panel.colCodigoPersona ?? 'Codigo'}</th>
                <th style={thStyle}>{panel.colFechaInicio ?? 'Fecha inicio'}</th>
                <th style={thStyle}>{panel.colEstado ?? 'Estado'}</th>
                <th style={{ ...thStyle, width: 100 }}>{panel.colAccion ?? 'Accion'}</th>
              </tr>
            </thead>
            <tbody>
              {asignaciones.map((a) => {
                const persona = a.persona;
                const activo = !a.fechaFin && (a.activo ?? true);
                return (
                  <tr key={a.id} style={{ background: '#fff' }}>
                    <td style={tdStyle}>
                      {persona ? `${persona.nombre} ${persona.apellidos}`.trim() : '-'}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 12,
                      }}
                    >
                      {persona?.codigo ?? '-'}
                    </td>
                    <td style={tdStyle}>{a.fechaInicio?.slice(0, 10) ?? '-'}</td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 10,
                          fontSize: 11,
                          fontWeight: 600,
                          background: activo ? '#ECFDF5' : '#FEF2F2',
                          color: activo ? '#16A34A' : '#DC2626',
                        }}
                      >
                        {activo
                          ? (panel.estadoActivo ?? 'Activo')
                          : (panel.estadoFinalizado ?? 'Finalizado')}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {activo && persona && (
                        <button
                          onClick={async () => {
                            try {
                              await request(`/apps/personas/${persona.id}/asignaciones/${a.id}`, {
                                method: 'PUT',
                                body: JSON.stringify({
                                  fechaFin: new Date().toISOString().split('T')[0],
                                }),
                              });
                              showToast(panel.asignacionFinalizada ?? 'Asignacion finalizada');
                              await onRefresh();
                            } catch {
                              showToast(panel.errorFinalizar ?? 'Error al finalizar');
                            }
                          }}
                          style={btnFinalizar}
                        >
                          {panel.finalizar ?? 'Finalizar'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showSub && (
        <AsignarPersonaSubPanel
          puestoId={detalle.id}
          puestoTitulo={detalle.titulo ?? ''}
          personasExcluidas={personasExcluidas}
          onClose={() => setShowSub(false)}
          onAssigned={async () => {
            setShowSub(false);
            showToast(panel.asignacionCreada ?? 'Persona asignada');
            await onRefresh();
          }}
        />
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '8px 10px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: 11,
  color: '#1F3864',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid #F0F0F0',
  fontSize: 13,
};

const btnFinalizar: React.CSSProperties = {
  padding: '4px 12px',
  border: '1px solid #DC2626',
  borderRadius: 6,
  background: 'transparent',
  color: '#DC2626',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
